// src/context/BrandingContext.tsx
//
// Runtime white-label branding. On first paint the SPA calls the PUBLIC,
// unauthenticated endpoint GET /api/branding/resolve?host=<hostname> and feeds
// the result into this context + a per-partner Mantine theme (see
// buildThemeFromBranding in theme.ts). No hardcoded "Verity" / logo asset in a
// component should read from anything but useBranding().
//
// Failure strategy (plan stap 2.1, three levels):
//   1. Network error / 5xx  → up to 3 retries with exponential backoff.
//   2. All retries fail     → fall back to the bundled default-branding.json,
//        but ONLY on the Verity-default hostnames (allowedHosts whitelist).
//        NEVER show Verity branding on a partner/unknown host — that actively
//        misleads the visitor. There we show a neutral, logo-less placeholder.
//   3. state === "fallback" → a discrete generic banner asks the user to
//        refresh (rendered by <BrandingFallbackBanner/>).
//
// See docs/plans-pending/2026-05-10-white-label-tech-foundation.md — stap 2.1.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import type {
  BrandingConfig,
  FirebaseWebConfig,
  LegalConfig,
  PublicBrandingResponse,
} from "@verity/shared-types";
import { withBase } from "@/api/client";
import { initFirebase, VERITY_ENV_FIREBASE_CONFIG } from "@/firebase";
import defaultBrandingJson from "@/branding/default-branding.json";

export type BrandingState = "loading" | "ready" | "fallback";

/** The branding half of the context (what the resolve produces). */
export type BrandingResolve = {
  branding: BrandingConfig;
  legal: LegalConfig;
  /** Magic-link sender display name — no template IDs / from-addresses. */
  emailFromName: string;
  /** The resolved partner's public Firebase web config, or null while loading /
   *  on a failed resolve (Firebase then inits from the Verity env config). */
  firebaseConfig: FirebaseWebConfig | null;
  /** The MCP server URL for this environment, from the BFF (the URL on the
   *  app's own host). `null` while loading, after a failed resolve, on a
   *  partner/unknown host, and from an older BFF that omits the key. Never
   *  defaulted to a bundled (prod) URL: a reader on staging/dev would then be
   *  shown the wrong host. Consumers render a neutral text on `null`. */
  mcpServerUrl: string | null;
  state: BrandingState;
};

export type BrandingContextValue = BrandingResolve & {
  /** True once Firebase Auth is initialised for the resolved project. Every
   *  Firebase consumer MUST gate its mount effects / calls on this (F2). */
  firebaseReady: boolean;
  /** Whether to offer Google sign-in: always on a Verity host; on a partner host
   *  only when the partner has its OWN Firebase project (else the Google popup
   *  would show "Verity" — so we hide it and offer magic-link only). */
  googleSignInEnabled: boolean;
};

// ── Bundled defaults ─────────────────────────────────────────────────────────

const DEFAULT_BRANDING: BrandingConfig =
  defaultBrandingJson.branding as BrandingConfig;
const DEFAULT_LEGAL: LegalConfig = defaultBrandingJson.legal as LegalConfig;
const DEFAULT_EMAIL_FROM_NAME: string = defaultBrandingJson.emailFromName;
const ALLOWED_HOSTS: string[] = defaultBrandingJson.allowedHosts ?? [];

const VERITY_DEFAULT: Omit<BrandingResolve, "state"> = {
  branding: DEFAULT_BRANDING,
  legal: DEFAULT_LEGAL,
  emailFromName: DEFAULT_EMAIL_FROM_NAME,
  firebaseConfig: null,
  // Deliberately null, and deliberately NOT in default-branding.json: the
  // bundle is the same on every environment, so any URL here would be wrong
  // on all but one of them.
  mcpServerUrl: null,
};

// Neutral, brand-agnostic placeholder shown on a partner/unknown host while the
// resolve is still in flight (so no "wrong" Verity logo flashes) and when the
// resolve fails there (so we never mislabel a partner's site as Verity).
const NEUTRAL_BRANDING: BrandingConfig = {
  productName: "Loading…",
  logoUrl: "/img/neutral-placeholder.svg",
  logoNoTextUrl: "/img/neutral-placeholder.svg",
  faviconUrl: "/img/neutral-placeholder.svg",
  primaryColor: "#6B7280", // neutral gray
};

const NEUTRAL_DEFAULT: Omit<BrandingResolve, "state"> = {
  branding: NEUTRAL_BRANDING,
  legal: { supportEmail: "" },
  emailFromName: "",
  firebaseConfig: null,
  mcpServerUrl: null,
};

function currentHost(): string {
  try {
    return window.location.hostname;
  } catch {
    return "";
  }
}

/** Whether a host is one of Verity's own domains (allowedHosts whitelist). */
function hostIsVerity(host: string): boolean {
  return ALLOWED_HOSTS.includes(host);
}

/**
 * Whether the CURRENT host is one of Verity's own domains. Consumers use this
 * for host-aware fallbacks: e.g. the login-background photo must stay the Verity
 * default on Verity's own hosts but must NEVER appear on a partner domain when a
 * partner hasn't supplied their own background.
 */
export function isVerityHost(): boolean {
  return hostIsVerity(currentHost());
}

/** Optimistic value while loading / after a failed resolve. */
function offlineValue(host: string): Omit<BrandingResolve, "state"> {
  return hostIsVerity(host) ? VERITY_DEFAULT : NEUTRAL_DEFAULT;
}

// ── Fetch with retry ─────────────────────────────────────────────────────────

const RETRY_DELAYS_MS = [250, 500, 1000];

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

async function fetchBranding(
  host: string,
  signal: AbortSignal
): Promise<PublicBrandingResponse> {
  let lastErr: unknown;
  // attempt 0 + one retry per delay
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      // Use the same API base as the rest of the app (withBase → VITE_API_URL).
      // A hardcoded relative "/api/…" would hit the SPA fallback (HTML) in the
      // split dev topology (frontend :5173 vs BFF :8080, no proxy).
      const res = await fetch(
        withBase(`/branding/resolve?host=${encodeURIComponent(host)}`),
        { signal, headers: { Accept: "application/json" } }
      );
      if (!res.ok) {
        throw new Error(`branding resolve returned ${res.status}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        // Misrouted (e.g. SPA HTML fallback) — fail clearly instead of a cryptic
        // JSON parse error, so retries/fallback behave predictably.
        throw new Error(`branding resolve: non-JSON response (${ct || "unknown"})`);
      }
      return (await res.json()) as PublicBrandingResponse;
    } catch (err) {
      if (signal.aborted) throw err;
      lastErr = err;
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt], signal);
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("branding resolve failed");
}

// ── Document / SEO meta (stap 2.4) ───────────────────────────────────────────
//
// Client-side meta updates only (MVP). Social crawlers that read before JS
// still see the generic index.html tags — an accepted limitation for
// login-walled partner domains (see plan stap 2.4; Edge SSR is a later phase).

function setLinkHref(id: string, href: string): void {
  const el = document.getElementById(id) as HTMLLinkElement | null;
  if (el && href) el.href = href;
}

function setMetaProperty(property: string, content: string): void {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function applyBrandingMeta(branding: BrandingConfig): void {
  if (typeof document === "undefined") return;
  if (branding.productName) document.title = branding.productName;
  setLinkHref("favicon", branding.faviconUrl);
  setLinkHref("apple-touch-icon", branding.faviconUrl);
  setMetaProperty("og:title", branding.productName);
  setMetaProperty("og:site_name", branding.productName);
  if (branding.ogImageUrl) setMetaProperty("og:image", branding.ogImageUrl);
}

// ── Context ──────────────────────────────────────────────────────────────────

const BrandingContext = createContext<BrandingContextValue>({
  ...VERITY_DEFAULT,
  state: "loading",
  firebaseReady: false,
  googleSignInEnabled: false,
});

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<BrandingResolve>(() => ({
    ...offlineValue(currentHost()),
    state: "loading",
  }));
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseProjectId, setFirebaseProjectId] = useState<string | null>(null);

  useEffect(() => {
    const host = currentHost();
    const controller = new AbortController();
    let active = true;

    fetchBranding(host, controller.signal)
      .then((resp) => {
        if (!active) return;
        setValue({
          branding: resp.branding,
          legal: resp.legal,
          emailFromName: resp.emailFromName,
          firebaseConfig: resp.firebaseConfig,
          // An older BFF omits the key: absent means null, never undefined.
          mcpServerUrl: resp.mcpServerUrl ?? null,
          state: "ready",
        });
      })
      .catch((err) => {
        if (!active || controller.signal.aborted) return;
        // Level 2: fall back to bundled default on Verity hosts only; neutral
        // (never Verity-branded) on a partner/unknown host.
        Sentry.captureMessage("branding_resolve_failed", {
          level: "error",
          tags: { event: "branding_resolve_failed" },
          extra: { host, error: err instanceof Error ? err.message : String(err) },
        });
        setValue({ ...offlineValue(host), state: "fallback" });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  // Per-partner Firebase (F2): init Firebase ONCE the partner has resolved (or the
  // resolve failed), NOT at module load. On "ready" use the resolved partner (or
  // Verity default) config; on "fallback" use the frontend's own Verity env config
  // so login still works. A malformed partner config degrades to the Verity env
  // config rather than white-screening. `firebaseReady` is the gate every Firebase
  // consumer waits on before touching auth.
  useEffect(() => {
    if (value.state === "loading" || firebaseReady) return;
    const primary =
      value.state === "ready" && value.firebaseConfig
        ? value.firebaseConfig
        : VERITY_ENV_FIREBASE_CONFIG;
    try {
      initFirebase(primary);
      setFirebaseProjectId(primary.projectId);
      setFirebaseReady(true);
    } catch (err) {
      // Malformed partner config → last-resort Verity env so login isn't bricked.
      try {
        initFirebase(VERITY_ENV_FIREBASE_CONFIG);
        setFirebaseProjectId(VERITY_ENV_FIREBASE_CONFIG.projectId);
        setFirebaseReady(true);
      } catch (err2) {
        Sentry.captureMessage("firebase_init_failed", {
          level: "error",
          tags: { event: "firebase_init_failed" },
          extra: { error: err2 instanceof Error ? err2.message : String(err2) },
        });
        // leave firebaseReady=false → login controls stay disabled (no crash).
      }
    }
  }, [value.state, value.firebaseConfig, firebaseReady]);

  // Stap 2.4 — reflect the resolved branding into <title>, favicon,
  // apple-touch-icon and OG tags whenever branding changes (loading → ready).
  useEffect(() => {
    applyBrandingMeta(value.branding);
  }, [value.branding]);

  // Offer Google sign-in on Verity's own hosts always; on a partner host only when
  // the partner has its OWN Firebase project (the active project isn't Verity's) —
  // otherwise the Google popup would show "Verity" on the partner's domain.
  const googleSignInEnabled =
    firebaseReady &&
    (hostIsVerity(currentHost()) ||
      firebaseProjectId !== VERITY_ENV_FIREBASE_CONFIG.projectId);

  return (
    <BrandingContext.Provider
      value={{ ...value, firebaseReady, googleSignInEnabled }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Discrete, non-blocking banner shown when branding resolution failed. Kept as
 * plain inline-styled markup so it renders regardless of whether a
 * MantineProvider sits above it. Rendered above <App/> in main.tsx.
 *
 * In-flow (not fixed) so it PUSHES the app down instead of overlaying the
 * header/menu, and dismissible via the × button.
 *
 * NOTE: the plan also envisions superadmin-only Sentry debug info here; that
 * needs the post-login AuthContext (which lives inside <App/>) and is deferred.
 */
export function BrandingFallbackBanner() {
  const { state } = useBranding();
  const [dismissed, setDismissed] = useState(false);
  if (state !== "fallback" || dismissed) return null;
  return (
    <div
      role="status"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        width: "100%",
        padding: "6px 40px",
        fontSize: 13,
        textAlign: "center",
        background: "#374151",
        color: "#F3F4F6",
        borderBottom: "1px solid #4B5563",
      }}
    >
      <span>We couldn&apos;t load all branding — please refresh the page.</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          color: "#F3F4F6",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
          padding: "0 4px",
        }}
      >
        ×
      </button>
    </div>
  );
}
